import React, { useId, useMemo } from 'react';
import Select, { SingleValue } from 'react-select';
import { RgbaColor } from 'react-colorful';

import renderType from 'containers/Legend/lineTypesRenderer';
import {
  LINE_TYPE_SELECT_OPTIONS,
  lineStyleSelectValueToSlug,
  lineStyleSlugToSelectValue,
} from 'containers/Legend/lineTypeOptions';
import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import { isValidHexColor } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  ActiveDefectLayer,
  MAX_ACTIVE_LAYER_LINE_WIDTH,
  MIN_ACTIVE_LAYER_LINE_WIDTH,
} from 'state/surveyDataManager';

import ActiveLayerBandLegend from './ActiveLayerBandLegend';

const DEFAULT_LINE_PREVIEW_COLOR: RgbaColor = { r: 48, g: 54, b: 150, a: 1 };

type LineStyleOption = {
  readonly value: string;
  readonly label: string;
};

function hexToRgba(hex: string): RgbaColor {
  const normalized = hex.trim().replace(/^#/, '');
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
    a: 1,
  };
}

function resolveLinePreviewColor(layer: ActiveDefectLayer): RgbaColor {
  const colorLow = layer.visualizationSettings?.colorLow;
  if (typeof colorLow === 'string' && isValidHexColor(colorLow)) {
    return hexToRgba(colorLow);
  }
  return DEFAULT_LINE_PREVIEW_COLOR;
}

export interface ActiveLayerBasicSettingsProps {
  readonly layer: ActiveDefectLayer;
  readonly defects: readonly DefectCatalogItem[];
  onDisplayNameChange: (displayName: string) => void;
  onOpacityChange: (opacity: number) => void;
  onLineWidthChange: (lineWidth: number) => void;
  onLineStyleChange: (lineStyle: string) => void;
  onHighlightToggle: () => void;
}

function ActiveLayerBasicSettings({
  layer,
  defects,
  onDisplayNameChange,
  onOpacityChange,
  onLineWidthChange,
  onLineStyleChange,
  onHighlightToggle,
}: ActiveLayerBasicSettingsProps): JSX.Element {
  const displayNameId = useId();
  const opacityId = useId();
  const lineWidthId = useId();
  const lineStyleId = useId();
  const lineStyleLabelId = useId();
  const highlightId = useId();

  const opacityPercent = Math.round(layer.opacity * 100);
  const linePreviewColor = useMemo(() => resolveLinePreviewColor(layer), [layer]);

  const selectedLineStyle = useMemo<LineStyleOption>(() => {
    const value = lineStyleSlugToSelectValue(layer.lineStyle);
    return (
      LINE_TYPE_SELECT_OPTIONS.find((option) => option.value === value) ?? {
        value,
        label: '',
      }
    );
  }, [layer.lineStyle]);

  const handleLineStyleChange = (
    option: SingleValue<LineStyleOption>,
  ): void => {
    if (!option) {
      return;
    }
    onLineStyleChange(lineStyleSelectValueToSlug(option.value));
  };

  return (
    <div className="sdm-active-layers__settings" aria-label={`Settings for ${layer.displayName}`}>
      <h3 className="sdm-active-layers__settings-title">Settings</h3>

      <label className="sdm-active-layers__field" htmlFor={displayNameId}>
        <span className="sdm-active-layers__field-label">Display name</span>
        <input
          id={displayNameId}
          type="text"
          className="sdm-active-layers__text-input"
          value={layer.displayName}
          onChange={(event) => onDisplayNameChange(event.target.value)}
        />
      </label>

      <label className="sdm-active-layers__field" htmlFor={opacityId}>
        <span className="sdm-active-layers__field-label">Opacity ({opacityPercent}%)</span>
        <input
          id={opacityId}
          type="range"
          className="sdm-active-layers__range-input"
          min={0}
          max={100}
          step={5}
          value={opacityPercent}
          onChange={(event) => onOpacityChange(Number(event.target.value) / 100)}
        />
      </label>

      <label className="sdm-active-layers__field" htmlFor={lineWidthId}>
        <span className="sdm-active-layers__field-label">Line width ({layer.lineWidth})</span>
        <input
          id={lineWidthId}
          type="range"
          className="sdm-active-layers__range-input"
          min={MIN_ACTIVE_LAYER_LINE_WIDTH}
          max={MAX_ACTIVE_LAYER_LINE_WIDTH}
          step={1}
          value={layer.lineWidth}
          onChange={(event) => onLineWidthChange(Number(event.target.value))}
        />
      </label>

      <div className="sdm-active-layers__field">
        <span className="sdm-active-layers__field-label" id={lineStyleLabelId}>
          Line style
        </span>
        <Select
          inputId={lineStyleId}
          aria-labelledby={lineStyleLabelId}
          className="custom_select sdm-active-layers__line-style-select"
          classNamePrefix="custom_select"
          options={LINE_TYPE_SELECT_OPTIONS}
          value={selectedLineStyle}
          onChange={handleLineStyleChange}
          isSearchable={false}
          menuPlacement="auto"
          formatOptionLabel={(lineType) => (
            <div className="sdm-active-layers__line-style-preview">
              {renderType(lineType.value, linePreviewColor)}
            </div>
          )}
        />
      </div>

      <label className="sdm-active-layers__checkbox" htmlFor={highlightId}>
        <input
          id={highlightId}
          type="checkbox"
          checked={layer.highlighted}
          onChange={onHighlightToggle}
        />
        <span>Highlight on map (bring to front)</span>
      </label>

      <ActiveLayerBandLegend layer={layer} defects={defects} />
    </div>
  );
}

export default ActiveLayerBasicSettings;
